import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { CheckCircle, Loader2, Info, Upload, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Appointment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const flashId = searchParams.get("flash");
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [images, setImages] = useState([]);
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    instagram: "",
    preference_contact: "email",
    type_rdv: flashId ? "flash" : "",
    creneau_souhaite: "",
    message: "",
    flash_id: flashId || "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setImages(prev => [...prev, ...newImages].slice(0, 5)); // Max 5 images
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.type_rdv) {
      toast.error("Veuillez sélectionner un type de rendez-vous.");
      return;
    }
    if (!formData.preference_contact) {
      toast.error("Veuillez indiquer votre préférence de contact.");
      return;
    }
    
    setLoading(true);

    try {
      // Convert images to base64
      const imagePromises = images.map(img => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve({
            name: img.name,
            data: reader.result
          });
          reader.onerror = reject;
          reader.readAsDataURL(img.file);
        });
      });
      
      const imagesBase64 = await Promise.all(imagePromises);
      
      await axios.post(`${API}/appointments`, {
        nom: formData.nom,
        email: formData.email,
        instagram: formData.instagram,
        creneau_souhaite: formData.creneau_souhaite,
        message: formData.message || null,
        flash_id: formData.flash_id || null,
        type_rdv: formData.type_rdv,
        preference_contact: formData.preference_contact,
        images: imagesBase64,
      });
      setSuccess(true);
      toast.success("Demande de rendez-vous envoyée !");
    } catch (error) {
      console.error(error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div data-testid="appointment-success" className="min-h-screen flex items-center justify-center">
        <div className="page-container text-center py-24">
          <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" strokeWidth={1} />
          <h1 className="text-3xl md:text-4xl font-heading font-light mb-4">
            Demande enregistrée !
          </h1>
          <p className="text-muted-foreground font-light max-w-md mx-auto mb-6">
            Votre demande de rendez-vous a bien été reçue. Je vous recontacterai pour confirmer le créneau et vous envoyer le lien de paiement pour l'acompte.
          </p>
          <div className="bg-secondary/50 p-6 border border-border/40 max-w-md mx-auto mb-8">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Prochaine étape :</strong> Après validation de votre demande, vous recevrez un email avec le lien pour régler l'acompte (entre 20€ et 100€ selon le tarif du tatouage).
            </p>
          </div>
          <Button onClick={() => navigate("/")} variant="outline" className="border-foreground text-foreground hover:bg-foreground hover:text-background">
            Retour à l'accueil
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="appointment-page">
      {/* Header */}
      <section className="page-header">
        <div className="page-container">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4 animate-fade-in-up">
            Rendez-vous
          </p>
          <h1 className="page-title animate-fade-in-up animation-delay-100">
            Prendre rendez-vous
          </h1>
          <p className="page-subtitle animate-fade-in-up animation-delay-200">
            {flashId 
              ? "Réservez votre créneau pour tatouer ce flash."
              : "Indiquez vos disponibilités et je vous recontacterai pour fixer une date."
            }
          </p>
        </div>
      </section>

      {/* Info Box */}
      <section className="pb-12">
        <div className="page-container">
          <div className="bg-secondary/50 p-6 border border-border/40 max-w-2xl">
            <div className="flex gap-4">
              <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div className="text-sm text-muted-foreground space-y-2">
                <p><strong className="text-foreground">Comment ça marche :</strong></p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Remplissez ce formulaire avec vos disponibilités</li>
                  <li>Je vous recontacte pour confirmer un créneau</li>
                  <li>Vous recevez un lien pour régler l'acompte (hors retouches et strass dentaire)</li>
                  <li>Votre rendez-vous est confirmé !</li>
                </ol>
                <p className="pt-2">
                  <strong className="text-foreground">Acompte :</strong> Un acompte est demandé pour confirmer votre rendez-vous (hors retouches et strass dentaire). Le montant varie entre 20€ et 100€ selon le tarif du tatouage, et sera déduit du prix total le jour du rendez-vous.
                </p>
                <p>
                  <strong className="text-foreground">Annulation :</strong> L'acompte n'est pas remboursable en cas d'annulation moins de 48h avant le rendez-vous (hors samedi). En cas de report avec préavis suffisant, l'acompte est conservé pour la nouvelle date (dans le mois).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="pb-24 md:pb-32">
        <div className="page-container">
          <form onSubmit={handleSubmit} className="max-w-2xl space-y-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <h3 className="text-lg font-heading border-b border-border/40 pb-2">
                Vos coordonnées
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-group">
                  <Label htmlFor="nom" className="form-label">Nom / Prénom *</Label>
                  <Input
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="form-input"
                    data-testid="input-nom"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="email" className="form-label">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="instagram" className="form-label">Instagram *</Label>
                <Input
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleChange}
                  required
                  placeholder="@votre_pseudo"
                  className="form-input"
                  data-testid="input-instagram"
                />
              </div>

              <div className="form-group">
                <Label className="form-label">Préférence de contact *</Label>
                <RadioGroup
                  value={formData.preference_contact}
                  onValueChange={(v) => handleSelectChange("preference_contact", v)}
                  className="flex gap-6 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="email" id="contact-email" />
                    <Label htmlFor="contact-email" className="cursor-pointer">Email</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="instagram" id="contact-instagram" />
                    <Label htmlFor="contact-instagram" className="cursor-pointer">Instagram</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Appointment Details */}
            <div className="space-y-6">
              <h3 className="text-lg font-heading border-b border-border/40 pb-2">
                Votre rendez-vous
              </h3>

              <div className="form-group">
                <Label className="form-label">Type de rendez-vous *</Label>
                <Select 
                  value={formData.type_rdv} 
                  onValueChange={(v) => handleSelectChange("type_rdv", v)} 
                  required
                >
                  <SelectTrigger className="form-input" data-testid="select-type">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flash">Flash</SelectItem>
                    <SelectItem value="strass-dentaire">Strass dentaire</SelectItem>
                    <SelectItem value="retouches">Retouches</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  Pour les projets personnalisés, merci de passer par la{" "}
                  <Link to="/demande-projet" className="underline hover:text-foreground transition-colors">
                    page demande de projet
                  </Link>.
                </p>
              </div>

              <div className="form-group">
                <Label htmlFor="creneau_souhaite" className="form-label">
                  Vos disponibilités *
                </Label>
                <Textarea
                  id="creneau_souhaite"
                  name="creneau_souhaite"
                  value={formData.creneau_souhaite}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Je suis disponible les mercredis et vendredis après-midi, ou le samedi toute la journée. Idéalement dans les 2-3 prochaines semaines."
                  className="form-textarea min-h-[120px]"
                  data-testid="input-creneau"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Indiquez les jours et horaires qui vous conviennent le mieux.
                </p>
              </div>

              <div className="form-group">
                <Label htmlFor="message" className="form-label">Message (optionnel)</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Informations complémentaires..."
                  className="form-textarea"
                  data-testid="input-message"
                />
              </div>

              {/* Image Upload */}
              <div className="form-group">
                <Label className="form-label">Photos (optionnel)</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Photos pour les retouches, screen du flash souhaité, inspirations pose de strass dentaire...
                </p>
                <div className="mt-2">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border hover:border-foreground cursor-pointer transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 text-muted-foreground mb-2" strokeWidth={1.5} />
                      <p className="text-sm text-muted-foreground">
                        Cliquez pour ajouter des images (max. 5)
                      </p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      data-testid="input-images"
                    />
                  </label>
                </div>
                
                {images.length > 0 && (
                  <div className="grid grid-cols-5 gap-2 mt-4">
                    {images.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img.preview}
                          alt={`Photo ${index + 1}`}
                          className="w-full aspect-square object-cover border border-border"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {flashId && (
                <div className="form-group">
                  <Label className="form-label">Flash sélectionné</Label>
                  <Input
                    value={flashId}
                    disabled
                    className="form-input bg-secondary/50"
                  />
                </div>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 uppercase tracking-widest text-xs w-full md:w-auto"
              disabled={loading}
              data-testid="submit-appointment"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                "Envoyer ma demande"
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Appointment;
